import { CLASSES_DE_EFEITO, type RESULTADOS_DE_ROTINA, type ClasseDeEfeito } from '@remoto/protocolo';
import {
  CriarRotinaSchema,
  CriarVerbeteSchema,
  jsonCanonico,
  type EstadoNoAlvo,
  type RotinaResumo,
  type VerbeteResumo,
  type VersaoDaRotina,
} from '@remoto/shared';
import type { z } from 'zod';

/**
 * As regras puras da tela do Toolkit (F3.7 da revisão de out/2026, ADR-0026):
 * os rótulos em pt-BR, a ordem e o filtro das listas, a situação de cada
 * versão no histórico e o diff de uma proposta contra a vigente.
 *
 * Fora dos componentes de propósito (`testes.md`: regra escondida em tela vira
 * função pura antes de ser testada). E nada aqui recalcula o ESTADO de uma
 * rotina num alvo: ele chega pronto da API (`estadoNoAlvo`, no `shared`), e
 * uma segunda escrita da precedência no painel envelheceria calada.
 */

export type FormaDaRotina = RotinaResumo['forma'];
export type ClasseDoVerbete = VerbeteResumo['classe'];
export type ResultadoDeRotina = (typeof RESULTADOS_DE_ROTINA)[number];

/** As listas fechadas vêm do CONTRATO, não de uma cópia: forma nova entra sozinha nos filtros. */
export const FORMAS: readonly FormaDaRotina[] = CriarRotinaSchema.shape.forma.options;
export const CLASSES_DO_VERBETE: readonly ClasseDoVerbete[] = CriarVerbeteSchema.shape.classe.options;

/*
  Os rótulos são `Record` sobre a lista fechada: um estado, classe ou forma
  novo no contrato sem frase aqui não compila — em vez de aparecer na tela como
  `so_ensaio` cru, que é o nome de uma variável e não uma palavra.
*/

export const ROTULO_DO_ESTADO: Record<EstadoNoAlvo, string> = {
  sem_versao_aprovada: 'sem versão aprovada',
  quebrada: 'quebrada',
  nao_se_aplica: 'não se aplica',
  suspeita: 'suspeita',
  validada: 'validada',
  so_ensaio: 'só ensaio',
};

/**
 * O que cada estado deixa rodar — a mesma regra de `podeRodar` (shared), dita
 * por extenso para quem passa o mouse no selo. Quem decide continua sendo a
 * API e o daemon; isto é só a frase.
 */
export const O_QUE_O_ESTADO_PERMITE: Record<EstadoNoAlvo, string> = {
  sem_versao_aprovada: 'Nada roda: a rotina só tem proposta, nenhuma versão aprovada.',
  quebrada: 'Nada roda neste alvo até alguém desmarcar a quebra, com motivo.',
  nao_se_aplica: 'Nada roda: a rotina não foi estendida a este alvo.',
  suspeita: 'Só fases de leitura, até um caso real ou uma sonda conferir de novo.',
  validada: 'Leitura e escrita interna. Ato externo ainda não roda pelo toolkit.',
  so_ensaio: 'Só fases de leitura, até o primeiro caso real conferido desta versão.',
};

/** A cor de cada coisa, sem nome de classe do Tailwind: quem pinta é a peça de tela. */
export type Tom = 'verde' | 'azul' | 'ambar' | 'vermelho' | 'cinza';

export const TOM_DO_ESTADO: Record<EstadoNoAlvo, Tom> = {
  sem_versao_aprovada: 'cinza',
  quebrada: 'vermelho',
  nao_se_aplica: 'cinza',
  suspeita: 'ambar',
  validada: 'verde',
  so_ensaio: 'azul',
};

export const ROTULO_DA_CLASSE: Record<ClasseDeEfeito, string> = {
  LEITURA: 'leitura',
  ESCRITA_INTERNA: 'escrita interna',
  ATO_EXTERNO: 'ato externo',
};

export const TOM_DA_CLASSE: Record<ClasseDeEfeito, Tom> = {
  LEITURA: 'cinza',
  ESCRITA_INTERNA: 'ambar',
  ATO_EXTERNO: 'vermelho',
};

export const ROTULO_DA_FORMA: Record<FormaDaRotina, string> = {
  FUNCAO: 'função',
  CRUD: 'cadastro (CRUD)',
  VARIANTE: 'variante',
  ROTEIRO: 'roteiro',
};

export const ROTULO_DA_CLASSE_DO_VERBETE: Record<ClasseDoVerbete, string> = {
  PORTA: 'porta',
  LIMITE: 'limite',
  ORDEM: 'ordem',
  ROTEIRO: 'roteiro',
  RECEITA: 'receita',
  OUTRO: 'outro',
};

export const ROTULO_DO_RESULTADO: Record<ResultadoDeRotina, string> = {
  CONFERIDO: 'conferido',
  FALHOU: 'falhou',
  NAO_CONFERIDO: 'não conferido',
  PENDENCIA_HUMANA: 'pendência humana',
  PAROU: 'parou',
};

/** O estado que veio do fio, ou `null` quando é um nome que esta tela não conhece. */
export function estadoConhecido(bruto: string): EstadoNoAlvo | null {
  return Object.hasOwn(ROTULO_DO_ESTADO, bruto) ? (bruto as EstadoNoAlvo) : null;
}

/** A classe que veio do fio, ou `null` — e `null` o cartão trata como o pior caso. */
export function classeConhecida(bruto: string): ClasseDeEfeito | null {
  return (CLASSES_DE_EFEITO as readonly string[]).includes(bruto) ? (bruto as ClasseDeEfeito) : null;
}

/** O resultado de uma validação, em palavra; o que a tela não conhece sai cru. */
export function rotuloDoResultado(bruto: string): string {
  return Object.hasOwn(ROTULO_DO_RESULTADO, bruto) ? ROTULO_DO_RESULTADO[bruto as ResultadoDeRotina] : bruto;
}

/**
 * Os 12 primeiros caracteres do hash: o bastante para conferir de olho que o
 * cartão e a tela falam da MESMA versão, sem uma linha de 64 letras que ninguém
 * lê. O inteiro fica no `title`.
 */
export function hashCurto(sha256: string): string {
  return sha256.slice(0, 12);
}

/* ------------------------------------------------------------ a lista */

/** O que pede olho numa rotina: alvos suspeitos ou quebrados e propostas sem decisão. */
export interface AtencaoDaRotina {
  suspeitas: string[];
  quebradas: string[];
  propostas: number;
}

export function atencaoDaRotina(r: RotinaResumo): AtencaoDaRotina {
  return {
    suspeitas: r.matriz.filter((c) => c.estado === 'suspeita').map((c) => c.alvo),
    quebradas: r.matriz.filter((c) => c.estado === 'quebrada').map((c) => c.alvo),
    propostas: r.propostasPendentes,
  };
}

/**
 * O peso na ordem: o que está em risco primeiro (suspeita ou quebrada — a
 * deriva que a JusAPI só via com o cliente esperando), depois o que espera
 * decisão, depois o resto, e o arquivado no fim.
 */
function pesoNaLista(r: RotinaResumo): number {
  if (r.arquivada) return 3;
  const a = atencaoDaRotina(r);
  if (a.suspeitas.length > 0 || a.quebradas.length > 0) return 0;
  if (a.propostas > 0) return 1;
  return 2;
}

export function ordenarRotinas(rotinas: readonly RotinaResumo[]): RotinaResumo[] {
  return [...rotinas].sort(
    (a, b) => pesoNaLista(a) - pesoNaLista(b) || a.titulo.localeCompare(b.titulo, 'pt-BR') || a.nome.localeCompare(b.nome),
  );
}

export interface FiltroDeRotinas {
  texto: string;
  forma: FormaDaRotina | null;
  classe: ClasseDeEfeito | null;
  alvo: string | null;
  estado: EstadoNoAlvo | null;
}

export const FILTRO_VAZIO: FiltroDeRotinas = { texto: '', forma: null, classe: null, alvo: null, estado: null };

/** Sem acento e sem caixa: "funcao" acha "Função", e ninguém digita acento no telefone. */
export function semAcento(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * O filtro da aba Rotinas, no CLIENTE.
 *
 * O catálogo é pequeno (dezenas, não milhares — o mesmo argumento do service),
 * e dois dos quatro filtros nem existem na API (classe e estado). Filtrar
 * aqui por cima da lista inteira deixa a resposta instantânea e numa chave de
 * consulta só.
 *
 * Estado com alvo escolhido é o estado NAQUELE alvo; sem alvo, em qualquer um.
 * `sem_versao_aprovada` também casa a rotina sem alvo nenhum: ela não tem
 * célula na matriz, e sumiria justamente do filtro que a procura.
 */
export function filtrarRotinas(rotinas: readonly RotinaResumo[], filtro: FiltroDeRotinas): RotinaResumo[] {
  const texto = semAcento(filtro.texto.trim());
  return rotinas.filter((r) => {
    if (texto && !semAcento(r.nome + ' ' + r.titulo).includes(texto)) return false;
    if (filtro.forma && r.forma !== filtro.forma) return false;
    if (filtro.classe && !r.classes.includes(filtro.classe)) return false;
    if (filtro.alvo && !r.matriz.some((c) => c.alvo === filtro.alvo)) return false;
    if (filtro.estado) {
      const celulas = filtro.alvo ? r.matriz.filter((c) => c.alvo === filtro.alvo) : r.matriz;
      const casa = celulas.some((c) => c.estado === filtro.estado);
      const semVigente = filtro.estado === 'sem_versao_aprovada' && r.vigente === null;
      if (!casa && !semVigente) return false;
    }
    return true;
  });
}

/** Quantos filtros estão ligados — para o "limpar" só aparecer quando há o que limpar. */
export function filtrosLigados(filtro: FiltroDeRotinas): number {
  return (
    (filtro.texto.trim() ? 1 : 0) +
    (filtro.forma ? 1 : 0) +
    (filtro.classe ? 1 : 0) +
    (filtro.alvo ? 1 : 0) +
    (filtro.estado ? 1 : 0)
  );
}

export function filtrarVerbetes(
  verbetes: readonly VerbeteResumo[],
  filtro: { texto: string; classe: ClasseDoVerbete | null },
): VerbeteResumo[] {
  const texto = semAcento(filtro.texto.trim());
  return verbetes
    .filter((v) => !filtro.classe || v.classe === filtro.classe)
    .filter((v) => !texto || semAcento(v.termo + ' ' + (v.vigente?.texto ?? '')).includes(texto))
    .sort((a, b) => b.propostasPendentes - a.propostasPendentes || a.numero - b.numero);
}

/* ------------------------------------------------------------ o histórico */

export type SituacaoDaVersao =
  | { tipo: 'vigente' }
  | { tipo: 'pendente' }
  | { tipo: 'recusada'; motivo: string | null }
  /** `por` nulo: aprovada um dia, e nenhuma aprovação posterior explica a troca. */
  | { tipo: 'superada'; por: { numero: number; motivo: string } | null };

const instante = (iso: string): number => Date.parse(iso);

/**
 * Onde uma versão está no histórico (R5: a superada fica com o motivo ao lado).
 *
 * "Superada por" é a PRÓXIMA versão aprovada depois desta — não só a que
 * declarou `substituiId`: uma proposta da mestre pode chegar sem ele, e a troca
 * aconteceu do mesmo jeito. O `superadaPor` da API fica de reserva para quando
 * nenhuma aprovação posterior existe.
 */
export function situacaoDaVersao(
  versao: VersaoDaRotina,
  vigenteId: string | null,
  versoes: readonly VersaoDaRotina[],
): SituacaoDaVersao {
  if (versao.id === vigenteId) return { tipo: 'vigente' };
  if (versao.decisao === null) return { tipo: 'pendente' };
  if (versao.decisao.decisao === 'RECUSADA') return { tipo: 'recusada', motivo: versao.decisao.motivo };

  const quando = instante(versao.decisao.em);
  let proxima: VersaoDaRotina | null = null;
  for (const outra of versoes) {
    if (outra.id === versao.id || outra.decisao?.decisao !== 'APROVADA') continue;
    const t = instante(outra.decisao.em);
    const depois = t > quando || (t === quando && outra.numero > versao.numero);
    if (!depois) continue;
    if (proxima === null || t < instante(proxima.decisao!.em)) proxima = outra;
  }
  if (proxima) return { tipo: 'superada', por: { numero: proxima.numero, motivo: proxima.motivo } };
  return { tipo: 'superada', por: versao.superadaPor ? { numero: versao.superadaPor.numero, motivo: versao.superadaPor.motivo } : null };
}

/** O histórico, da mais nova para a mais velha: quem abre quer saber o que mudou por último. */
export function historicoEmOrdem(versoes: readonly VersaoDaRotina[]): VersaoDaRotina[] {
  return [...versoes].sort((a, b) => b.numero - a.numero);
}

/* ------------------------------------------------------------ o diff */

export interface DiferencaDeArquivo {
  caminho: string;
  situacao: 'novo' | 'removido' | 'alterado' | 'igual';
  antes: string;
  depois: string;
}

const ORDEM_DA_SITUACAO: Record<DiferencaDeArquivo['situacao'], number> = { alterado: 0, novo: 1, removido: 2, igual: 3 };

/**
 * Os arquivos da proposta contra os da vigente, um por caminho.
 *
 * O que mudou vem primeiro: quem revisa uma proposta de vinte arquivos com uma
 * linha trocada num deles não pode ter de abrir os vinte para achar a linha.
 * Sem vigente (rotina nova da mestre), tudo é "novo".
 */
export function diferencasDeArquivos(
  vigente: readonly { caminho: string; conteudo: string }[] | null,
  proposta: readonly { caminho: string; conteudo: string }[],
): DiferencaDeArquivo[] {
  const antes = new Map((vigente ?? []).map((a) => [a.caminho, a.conteudo]));
  const depois = new Map(proposta.map((a) => [a.caminho, a.conteudo]));
  const caminhos = [...new Set([...antes.keys(), ...depois.keys()])];
  return caminhos
    .map((caminho): DiferencaDeArquivo => {
      const a = antes.get(caminho);
      const d = depois.get(caminho);
      if (a === undefined) return { caminho, situacao: 'novo', antes: '', depois: d ?? '' };
      if (d === undefined) return { caminho, situacao: 'removido', antes: a, depois: '' };
      return { caminho, situacao: a === d ? 'igual' : 'alterado', antes: a, depois: d };
    })
    .sort((x, y) => ORDEM_DA_SITUACAO[x.situacao] - ORDEM_DA_SITUACAO[y.situacao] || x.caminho.localeCompare(y.caminho));
}

/**
 * O manifesto em JSON CANÔNICO e indentado, para os dois lados do diff.
 *
 * O Postgres devolve o `jsonb` com as chaves na ordem dele; comparar o texto
 * cru acusaria diferença onde não há nenhuma. O canônico é o MESMO que entra
 * no hash da versão (`jsonCanonico`, do shared).
 */
export function manifestoLegivel(manifesto: unknown): string {
  if (manifesto === null || manifesto === undefined) return '';
  return JSON.stringify(JSON.parse(jsonCanonico(manifesto)), null, 2);
}

export interface MudancaDoManifesto {
  texto: string;
  /**
   * Muda o que a rotina PODE fazer: classe que sobe de risco, critério de
   * sucesso mexido (R23: na JusAPI, um caso afrouxou o critério e ninguém viu),
   * ponto sem retorno, rodar na VPS. A tela pinta de vermelho.
   */
  grave: boolean;
}

const ROTULO_DA_CHAVE: Record<string, string> = {
  versaoDoManifesto: 'versão do manifesto',
  interpretador: 'interpretador',
  argumentosDoInterpretador: 'argumentos do interpretador',
  entrada: 'arquivo de entrada',
  onde: 'onde roda',
  dependencias: 'dependências',
  requisitos: 'requisitos',
  recursos: 'recursos',
  ritmoPorAlvoSeg: 'ritmo por alvo (s)',
  sondaACadaHoras: 'frequência da sonda (h)',
};

type Objeto = Record<string, unknown>;

const objeto = (v: unknown): Objeto => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Objeto) : {});
const igual = (a: unknown, b: unknown): boolean => jsonCanonico(a ?? null) === jsonCanonico(b ?? null);
const primitivo = (v: unknown): v is string | number | boolean =>
  typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
const risco = (classe: unknown): number => (CLASSES_DE_EFEITO as readonly unknown[]).indexOf(classe);
const rotuloDaClasseCrua = (classe: unknown): string =>
  typeof classe === 'string' && classeConhecida(classe) ? ROTULO_DA_CLASSE[classe as ClasseDeEfeito] : String(classe);

/**
 * O que mudou no manifesto, em frases — por cima do diff de texto, não no
 * lugar dele. O diff mostra tudo; isto diz o que importa ANTES de ler o diff:
 * uma fase que virou ato externo é uma linha de JSON no meio de cem.
 *
 * Lista vazia com vigente = o manifesto não mudou. Sem vigente não há o que
 * comparar, e a lista também volta vazia (quem chama diz "primeira versão").
 */
export function mudancasDoManifesto(vigente: unknown, proposta: unknown): MudancaDoManifesto[] {
  if (vigente === null || vigente === undefined) return [];
  const a = objeto(vigente);
  const b = objeto(proposta);
  const saida: MudancaDoManifesto[] = [];

  const chaves = [...new Set([...Object.keys(a), ...Object.keys(b)])].filter((c) => c !== 'fases').sort();
  for (const chave of chaves) {
    if (igual(a[chave], b[chave])) continue;
    const rotulo = ROTULO_DA_CHAVE[chave] ?? chave;
    const texto =
      (primitivo(a[chave]) || a[chave] === undefined) && (primitivo(b[chave]) || b[chave] === undefined)
        ? `${rotulo}: ${String(a[chave] ?? '—')} → ${String(b[chave] ?? '—')}`
        : `${rotulo} mudou`;
    const passaAVps = chave === 'onde' && objeto(b[chave])['vps'] === true && objeto(a[chave])['vps'] !== true;
    saida.push({ texto: passaAVps ? 'passa a rodar na VPS' : texto, grave: passaAVps });
  }

  const fases = (m: Objeto): Map<string, Objeto> =>
    new Map((Array.isArray(m['fases']) ? m['fases'] : []).map((f) => [String(objeto(f)['nome']), objeto(f)]));
  const fa = fases(a);
  const fb = fases(b);
  for (const [nome, f] of fb) {
    const antes = fa.get(nome);
    if (!antes) {
      saida.push({ texto: `fase nova: ${nome} (${rotuloDaClasseCrua(f['classe'])})`, grave: f['classe'] !== 'LEITURA' });
      continue;
    }
    if (!igual(antes['classe'], f['classe'])) {
      saida.push({
        texto: `fase ${nome}: ${rotuloDaClasseCrua(antes['classe'])} → ${rotuloDaClasseCrua(f['classe'])}`,
        grave: risco(f['classe']) > risco(antes['classe']) || risco(f['classe']) < 0,
      });
    }
    if (!igual(antes['criterioDeSucesso'], f['criterioDeSucesso'])) {
      saida.push({ texto: `fase ${nome}: o critério de sucesso mudou`, grave: true });
    }
    if (!igual(antes['pontoSemRetorno'], f['pontoSemRetorno'])) {
      saida.push({ texto: `fase ${nome}: o ponto sem retorno mudou`, grave: true });
    }
    if (!igual(antes['timeoutSeg'], f['timeoutSeg'])) {
      saida.push({ texto: `fase ${nome}: tempo máximo ${String(antes['timeoutSeg'])} s → ${String(f['timeoutSeg'])} s`, grave: false });
    }
    if (!igual(antes['parametros'], f['parametros'])) {
      saida.push({ texto: `fase ${nome}: os parâmetros mudaram`, grave: false });
    }
  }
  for (const nome of fa.keys()) {
    if (!fb.has(nome)) saida.push({ texto: `fase removida: ${nome}`, grave: false });
  }
  return saida;
}

/* ------------------------------------------------------------ o formulário */

/**
 * Confere um corpo contra o MESMO schema que a API usa na borda, e diz o que
 * está errado em pt-BR, com o CAMPO na frente.
 *
 * Conferir antes de mandar não substitui a API (que confere de novo): poupa a
 * ida e volta e, principalmente, diz QUAL campo — o 400 da borda devolve
 * `validacao`, e a tela não mostra o texto cru do servidor (`web.md`).
 *
 * As mensagens de fábrica do zod saem em inglês ("Too small"); as nossas
 * (`refine` e `regex` do contrato) já vêm em português e passam como estão.
 * `reportInput` é o que deixa separar "faltou" de "veio errado".
 */
export function validar<T>(
  schema: z.ZodType<T>,
  corpo: unknown,
): { ok: true; dados: T } | { ok: false; problemas: string[] } {
  const r = schema.safeParse(corpo, { reportInput: true });
  return r.success ? { ok: true, dados: r.data } : { ok: false, problemas: problemasDoCorpo(r.error) };
}

export function problemasDoCorpo(erro: z.ZodError): string[] {
  return erro.issues.map((p) => {
    const campo = p.path.length > 0 ? p.path.join('.') : 'corpo';
    switch (p.code) {
      case 'invalid_type':
        return p.input === undefined ? `${campo}: obrigatório` : `${campo}: tipo errado`;
      case 'too_small':
        if (p.origin === 'array') return `${campo}: precisa de pelo menos ${String(p.minimum)}`;
        if (p.origin === 'string') return `${campo}: não pode ficar vazio`;
        return `${campo}: abaixo do mínimo (${String(p.minimum)})`;
      case 'too_big':
        if (p.origin === 'array') return `${campo}: no máximo ${String(p.maximum)}`;
        if (p.origin === 'string') return `${campo}: passa do tamanho máximo (${String(p.maximum)} caracteres)`;
        return `${campo}: acima do máximo (${String(p.maximum)})`;
      case 'invalid_value':
        return `${campo}: valor fora da lista`;
      case 'invalid_union':
        return `${campo}: forma não reconhecida`;
      case 'unrecognized_keys':
        return `${campo}: campo desconhecido (${p.keys.join(', ')})`;
      case 'invalid_format':
      case 'custom':
        // As nossas frases: o `regex` e o `refine` do contrato falam português.
        // Um `regex` sem frase própria cai na de fábrica, que é inglês.
        return `${campo}: ${/^Invalid/.test(p.message) ? 'formato inválido' : p.message}`;
      default:
        return `${campo}: inválido`;
    }
  });
}

/** O JSON do manifesto digitado, ou a frase do que está errado nele. */
export function lerJson(texto: string): { ok: true; valor: unknown } | { ok: false; erro: string } {
  try {
    return { ok: true, valor: JSON.parse(texto) };
  } catch (e) {
    return { ok: false, erro: 'o manifesto não é um JSON válido' + (e instanceof SyntaxError ? ` (${e.message})` : '') };
  }
}

/** O manifesto de partida de uma rotina nova: uma fase de leitura, que é o que a fase 1 roda. */
export const MANIFESTO_DE_PARTIDA = {
  versaoDoManifesto: 1,
  interpretador: 'python',
  entrada: 'rotina.py',
  onde: { pc: true, vps: false },
  requisitos: [],
  fases: [
    {
      nome: 'consultar',
      classe: 'LEITURA',
      timeoutSeg: 60,
      parametros: [],
      criterioDeSucesso: [{ campo: 'itens', regra: 'presente' }],
    },
  ],
  recursos: [],
  ritmoPorAlvoSeg: 0,
};

/** A entrada de partida: escreve o resultado normalizado na última linha do stdout. */
export const ENTRADA_DE_PARTIDA =
  'import json\n\n' +
  '# A ultima linha JSON do stdout e o resultado (o daemon le e confere contra o contrato).\n' +
  'print(json.dumps({"resultado": "CONFERIDO", "motivo": "ok", "dados": {"itens": []}}))\n';
